import { HttpClient } from '@angular/common/http'
import { inject, Injectable } from '@angular/core'
import { QueryObserverResult } from '@tanstack/query-core'
import { map, Observable, tap } from 'rxjs'
import { Call } from '../../calls.service'
import { DownloadsService } from '../../downloads.service'
import { injectMutation, injectQuery, injectQueryClient } from '../../query'
import { queryKeys } from '../../query-keys'
import { ToastService } from '../../toast.service'
import { generateFileName, generateHeadersWithTimezone } from '../../utils'

export type ActionItemType = 'AGREED' | 'COMMITTED' | 'PROPOSED'

export type ActionItemAssignee = {
  id: number
  name: string
  callAgendaAttendeeId: number | null
}

export type ActionItemPassage = {
  startSentenceId: string
  endSentenceId: string
  startTimeSeconds: number
  text: string
}

export type ActionItem = {
  id: number
  description: string
  type: ActionItemType
  dueText: string | null
  dueDate: string | null
  isUserCreated: boolean
  isMarkedAsNotAnAction: boolean
  editedAt: string | null
  assignees: ActionItemAssignee[]
  passages: ActionItemPassage[]
}

export type ActionItemDetectionStatus = 'NOT_STARTED' | 'ONGOING' | 'SUCCEEDED' | 'FAILED'

export type ActionItemsOverview = {
  detectionStatus: ActionItemDetectionStatus
  items: ActionItem[]
}

const DETECTION_POLL_INTERVAL_MS = 5000

export type ActionItemAssigneeInput = {
  name: string
  callAgendaAttendeeId: number | null
}

export type SaveActionItem = {
  description: string
  type: ActionItemType
  assignees: ActionItemAssigneeInput[]
  dueText: string | null
  dueDate: string | null
}

export function toSaveActionItem(item: ActionItem): SaveActionItem {
  return {
    description: item.description,
    type: item.type,
    assignees: item.assignees.map(({ name, callAgendaAttendeeId }) => ({ name, callAgendaAttendeeId })),
    dueText: item.dueText,
    dueDate: item.dueDate,
  }
}

@Injectable({ providedIn: 'root' })
export class ActionItemsService {
  private http = inject(HttpClient)
  private query = injectQuery()
  private mutation = injectMutation()
  private queryClient = injectQueryClient()
  private toastService = inject(ToastService)
  private downloadsService = inject(DownloadsService)

  list(callId: number): Observable<QueryObserverResult<ActionItemsOverview>> {
    return this.query({
      queryKey: queryKeys.calls.actionItems.byCallId(callId),
      queryFn: () => this.http.get<ActionItemsOverview>(`/api/calls/${callId}/action-items`),
      refetchInterval: (query) => (query.state.data?.detectionStatus === 'ONGOING' ? DETECTION_POLL_INTERVAL_MS : false),
    }).result$
  }

  refresh(callId: number) {
    this.queryClient.invalidateQueries({ queryKey: queryKeys.calls.actionItems.byCallId(callId) })
  }

  /** Downloads the call's detected action items. */
  export(call: Call, format: 'xlsx' | 'csv'): Observable<void> {
    const fileName = generateFileName(call, 'action-items', format)
    return this.http
      .get(`/api/calls/${call.id}/action-items/export`, { responseType: 'blob', headers: generateHeadersWithTimezone(), params: { format } })
      .pipe(
        tap((data) => {
          this.downloadsService.downloadData(data, fileName, format)
          this.toastService.show('CALLS.ACTION_ITEMS_EXPORT_COMPLETED')
        }),
        map(() => void 0)
      )
  }

  createMutation() {
    return this.actionItemMutation(({ callId, data }: { callId: number; data: SaveActionItem }) =>
      this.http.post<ActionItem>(`/api/calls/${callId}/action-items`, data)
    )
  }

  updateMutation() {
    return this.actionItemMutation(({ callId, actionItemId, data }: { callId: number; actionItemId: number; data: SaveActionItem }) =>
      this.http.put<ActionItem>(`/api/calls/${callId}/action-items/${actionItemId}`, data)
    )
  }

  markAsNotAnActionMutation() {
    return this.actionItemMutation(
      ({ callId, actionItemId, isMarkedAsNotAnAction }: { callId: number; actionItemId: number; isMarkedAsNotAnAction: boolean }) =>
        this.http.put<ActionItem>(`/api/calls/${callId}/action-items/${actionItemId}/not-an-action`, { isMarkedAsNotAnAction })
    )
  }

  addDetailMutation() {
    return this.actionItemMutation(
      ({ callId, actionItemId }: { callId: number; actionItemId: number }) =>
        this.http.post<ActionItem>(`/api/calls/${callId}/action-items/${actionItemId}/add-detail`, {}),
      'ACTION_ITEMS.ADD_DETAIL_FAILED'
    )
  }

  /** Replaces the item in the cached list straight away, so the row doesn't wait for the refetch. */
  private actionItemMutation<TVariables extends { callId: number }>(
    mutationFn: (variables: TVariables) => Observable<ActionItem>,
    errorToastKey: string = 'ACTION_ITEMS.UPDATE_FAILED'
  ) {
    return this.mutation({
      mutationFn,
      onSuccess: (item, { callId }) => {
        this.queryClient.setQueryData<ActionItemsOverview>(queryKeys.calls.actionItems.byCallId(callId), (overview) =>
          overview === undefined
            ? undefined
            : {
                ...overview,
                items: overview.items.some((existing) => existing.id === item.id)
                  ? overview.items.map((existing) => (existing.id === item.id ? item : existing))
                  : [...overview.items, item],
              }
        )
        this.queryClient.invalidateQueries({ queryKey: queryKeys.calls.actionItems.byCallId(callId) })
      },
      onError: () => {
        this.toastService.show(errorToastKey)
      },
    })
  }
}
